import React, { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

export default function CareersAdminPage() {
  const [tab, setTab] = useState('listings'); // listings | applications
  const [listings, setListings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` };

  function emptyForm() {
    return { title: '', department: '', location: 'Remote / Stevenage', type: 'full-time', salary_range: '', description: '', requirements: '' };
  }

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/careers/admin/all`, { headers });
      setListings(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/careers/applications/all`, { headers });
      setApplications(await res.json());
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (tab === 'listings') fetchListings();
    else fetchApplications();
  }, [tab, fetchListings, fetchApplications]);

  const handleSave = async () => {
    try {
      const url = editingListing
        ? `${API}/api/careers/admin/${editingListing.id}`
        : `${API}/api/careers/admin`;
      const method = editingListing ? 'PUT' : 'POST';

      await fetch(url, { method, headers, body: JSON.stringify(form) });
      setShowForm(false);
      setEditingListing(null);
      setForm(emptyForm());
      fetchListings();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (listing) => {
    setEditingListing(listing);
    setForm({
      title: listing.title || '',
      department: listing.department || '',
      location: listing.location || '',
      type: listing.type || 'full-time',
      salary_range: listing.salary_range || '',
      description: listing.description || '',
      requirements: listing.requirements || '',
    });
    setShowForm(true);
  };

  const handleToggleActive = async (listing) => {
    await fetch(`${API}/api/careers/admin/${listing.id}`, {
      method: 'PUT', headers,
      body: JSON.stringify({ is_active: !listing.is_active })
    });
    fetchListings();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this listing permanently?')) return;
    await fetch(`${API}/api/careers/admin/${id}`, { method: 'DELETE', headers });
    fetchListings();
  };

  const handleAppStatus = async (id, status) => {
    await fetch(`${API}/api/careers/applications/${id}`, {
      method: 'PATCH', headers,
      body: JSON.stringify({ status })
    });
    fetchApplications();
  };

  const appStatusColors = {
    new: { bg: '#DBEAFE', color: '#2563EB' },
    reviewing: { bg: '#FEF3C7', color: '#D97706' },
    shortlisted: { bg: '#D1FAE5', color: '#059669' },
    rejected: { bg: '#FEE2E2', color: '#DC2626' },
  };

  return (
    <div style={{ padding: '32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Careers Management</h1>
        {tab === 'listings' && (
          <button onClick={() => { setShowForm(true); setEditingListing(null); setForm(emptyForm()); }} style={btnPrimary}>
            <i className="fas fa-plus" style={{ marginRight: 8 }}></i>Add Listing
          </button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: '#F3F4F6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {['listings', 'applications'].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
              background: tab === t ? 'white' : 'transparent',
              color: tab === t ? '#111827' : '#6B7280',
              boxShadow: tab === t ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
              textTransform: 'capitalize',
            }}
          >
            {t === 'listings' ? `Job Listings (${listings.length})` : `Applications (${applications.length})`}
          </button>
        ))}
      </div>

      {/* CREATE / EDIT FORM MODAL */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 20, padding: 32, maxWidth: 640, width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>
              {editingListing ? 'Edit Listing' : 'New Job Listing'}
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <FormField label="Job Title *" value={form.title} onChange={v => setForm({ ...form, title: v })} placeholder="e.g. Chief Operating Officer" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <FormField label="Department" value={form.department} onChange={v => setForm({ ...form, department: v })} placeholder="e.g. Operations" />
                <FormField label="Location" value={form.location} onChange={v => setForm({ ...form, location: v })} placeholder="e.g. Remote / Stevenage" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={labelStyle}>Type</label>
                  <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={fieldStyle}>
                    <option value="full-time">Full-time</option>
                    <option value="part-time">Part-time</option>
                    <option value="contract">Contract</option>
                  </select>
                </div>
                <FormField label="Salary Range" value={form.salary_range} onChange={v => setForm({ ...form, salary_range: v })} placeholder="e.g. £60,000 - £80,000" />
              </div>
              <div>
                <label style={labelStyle}>Description *</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={6} style={{ ...fieldStyle, resize: 'vertical' }} placeholder="Full job description..." />
              </div>
              <div>
                <label style={labelStyle}>Requirements</label>
                <textarea value={form.requirements} onChange={e => setForm({ ...form, requirements: e.target.value })} rows={4} style={{ ...fieldStyle, resize: 'vertical' }} placeholder="Key requirements (one per line)..." />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowForm(false); setEditingListing(null); }} style={btnSecondary}>Cancel</button>
              <button onClick={handleSave} disabled={!form.title || !form.description} style={btnPrimary}>
                {editingListing ? 'Save Changes' : 'Create Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LISTINGS TAB */}
      {tab === 'listings' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <Placeholder text="Loading..." />
          ) : listings.length === 0 ? (
            <Placeholder text="No job listings yet. Create your first one!" />
          ) : (
            listings.map(l => (
              <div key={l.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{l.title}</h3>
                      <span style={{
                        padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                        background: l.is_active ? '#D1FAE5' : '#FEE2E2',
                        color: l.is_active ? '#059669' : '#DC2626'
                      }}>
                        {l.is_active ? 'Live' : 'Hidden'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#6B7280' }}>
                      {l.department && <span><i className="fas fa-building" style={{ marginRight: 4 }}></i>{l.department}</span>}
                      <span><i className="fas fa-map-marker-alt" style={{ marginRight: 4 }}></i>{l.location}</span>
                      <span><i className="fas fa-clock" style={{ marginRight: 4 }}></i>{l.type}</span>
                      {l.salary_range && <span><i className="fas fa-pound-sign" style={{ marginRight: 4 }}></i>{l.salary_range}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <IconBtn icon="fa-eye" title={l.is_active ? 'Hide' : 'Show'} color="#6B7280" onClick={() => handleToggleActive(l)} />
                    <IconBtn icon="fa-edit" title="Edit" color="#3B82F6" onClick={() => handleEdit(l)} />
                    <IconBtn icon="fa-trash" title="Delete" color="#EF4444" onClick={() => handleDelete(l.id)} />
                  </div>
                </div>
                <p style={{ fontSize: 14, color: '#6B7280', marginTop: 10, lineHeight: 1.6 }}>
                  {l.description?.substring(0, 200)}{l.description?.length > 200 ? '...' : ''}
                </p>
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
                  Created {new Date(l.created_at).toLocaleDateString('en-GB')}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* APPLICATIONS TAB */}
      {tab === 'applications' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {loading ? (
            <Placeholder text="Loading..." />
          ) : applications.length === 0 ? (
            <Placeholder text="No applications received yet." />
          ) : (
            applications.map(app => {
              const sc = appStatusColors[app.status] || appStatusColors.new;
              return (
                <div key={app.id} style={{ background: 'white', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{app.name}</h3>
                      <div style={{ display: 'flex', gap: 16, fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                        <a href={`mailto:${app.email}`} style={{ color: '#3B82F6', textDecoration: 'none' }}>{app.email}</a>
                        {app.phone && <span>{app.phone}</span>}
                      </div>
                      {app.career_title && (
                        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                          Applied for: <strong>{app.career_title}</strong>
                        </div>
                      )}
                    </div>
                    <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>
                      {app.status}
                    </span>
                  </div>

                  {app.cover_message && (
                    <p style={{ fontSize: 14, color: '#374151', marginTop: 12, lineHeight: 1.6, background: '#F9FAFB', padding: 16, borderRadius: 10 }}>
                      {app.cover_message}
                    </p>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #F3F4F6' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {['new', 'reviewing', 'shortlisted', 'rejected'].map(s => (
                        <button
                          key={s}
                          onClick={() => handleAppStatus(app.id, s)}
                          style={{
                            padding: '4px 12px', borderRadius: 6, border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: 12,
                            fontWeight: 500, textTransform: 'capitalize',
                            background: app.status === s ? (appStatusColors[s]?.bg || '#F3F4F6') : 'white',
                            color: app.status === s ? (appStatusColors[s]?.color || '#374151') : '#6B7280',
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                    {app.cv_filename && (
                      <a
                        href={`${API}/api/careers/applications/${app.id}/cv`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#3B82F6', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}
                      >
                        <i className="fas fa-file-pdf"></i>{app.cv_filename}
                      </a>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 8 }}>
                    Applied {new Date(app.created_at).toLocaleDateString('en-GB')}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={fieldStyle} />
    </div>
  );
}

function IconBtn({ icon, title, color, onClick }) {
  return (
    <button onClick={onClick} title={title} style={{ background: 'none', border: 'none', cursor: 'pointer', color, fontSize: 14, padding: 6 }}>
      <i className={`fas ${icon}`}></i>
    </button>
  );
}

function Placeholder({ text }) {
  return <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', background: 'white', borderRadius: 16, border: '1px solid #E5E7EB' }}>{text}</div>;
}

const btnPrimary = { display: 'inline-flex', alignItems: 'center', background: 'linear-gradient(135deg,#10B981,#3B82F6)', color: 'white', padding: '10px 20px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 };
const btnSecondary = { padding: '10px 20px', borderRadius: 10, border: '1px solid #E5E7EB', background: 'white', cursor: 'pointer', fontWeight: 600, fontSize: 14, color: '#374151' };
const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const fieldStyle = { width: '100%', padding: '10px 14px', border: '1.5px solid #E5E7EB', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', background: '#F9FAFB' };
